import { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Modal overlay that renders to <body> via React portal — escapes any
 * transformed/filtered ancestor (e.g. our `.fade-up` page wrappers) that
 * would otherwise trap a `position:fixed` overlay inside its containing
 * block.
 *
 * Usage:
 *   <Modal onClose={handler} testId="my-modal" widthClass="max-w-3xl">
 *     ...modal content (no need for the outer overlay div)...
 *   </Modal>
 */
export default function Modal({
  onClose,
  testId,
  widthClass = "max-w-2xl",
  zIndex = 60,
  children,
  scrollable = true,
}) {
  // Lock body scroll while the modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape" && onClose) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const card = `cc-card ${widthClass} w-full p-8 fade-up${scrollable ? " max-h-[90vh] overflow-y-auto cc-scroll" : ""}`;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-6"
      style={{
        background: "rgba(26, 28, 25, 0.45)",
        backdropFilter: "blur(2px)",
        zIndex,
      }}
      onClick={onClose}
      data-testid={testId ? `${testId}-overlay` : undefined}
    >
      <div
        className={card}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
